import { IntegrationCore } from 'types/Integration';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import { isQuoteRequestActionable } from 'utils/isQuoteRequestActionable';

import { QuoteMode } from '../../types';
import { BATCH_LTL_MIN_SHIPMENTS } from '../constants';

type BatchLTLAvailabilityParams = {
  isLTLQuickQuoteEnabled: boolean;
  ltlTMSIntegrations: Maybe<IntegrationCore[]>;
};

// Batch LTL rates with the Tai LTL form; 3G and Turvo take priority in the single LTL form.
export function isBatchLTLQuoteAvailable({
  isLTLQuickQuoteEnabled,
  ltlTMSIntegrations,
}: BatchLTLAvailabilityParams): boolean {
  if (!isLTLQuickQuoteEnabled || !ltlTMSIntegrations?.length) {
    return false;
  }

  const names = ltlTMSIntegrations.map((integration) => integration.name);

  return (
    names.includes(TMS.Tai) &&
    !names.includes(TMS.ThreeG) &&
    !names.includes(TMS.Turvo)
  );
}

// Pending LTL quote requests extracted from one email, in extraction order. Returns []
// unless at least two came from the same email.
export function getBatchLTLSuggestions(
  suggestions: GenericSuggestion[],
  emailId?: number | null
): SuggestedQuoteChange[] {
  const suggestionsByEmailId = new Map<number, SuggestedQuoteChange[]>();

  suggestions
    .filter(isPendingLTLQuoteSuggestion)
    .filter((suggestion) => emailId == null || suggestion.emailID === emailId)
    .forEach((suggestion) => {
      const group = suggestionsByEmailId.get(suggestion.emailID) ?? [];
      group.push(suggestion);
      suggestionsByEmailId.set(suggestion.emailID, group);
    });

  const largestGroup = [...suggestionsByEmailId.values()].reduce<
    SuggestedQuoteChange[]
  >((largest, group) => (group.length > largest.length ? group : largest), []);

  return largestGroup.length >= BATCH_LTL_MIN_SHIPMENTS
    ? sortBatchLTLSuggestionsBySourceOrder(largestGroup)
    : [];
}

// The suggestions endpoint normally returns newest quote requests first. Batch LTL lanes
// must follow the shipment order from the source email instead.
export function sortBatchLTLSuggestionsBySourceOrder(
  suggestions: SuggestedQuoteChange[]
): SuggestedQuoteChange[] {
  return suggestions
    .map((suggestion, position) => ({
      suggestion,
      sourceIndex:
        suggestion.suggested?.batchLtlShipment?.index ?? position + 1,
      position,
    }))
    .sort((a, b) => a.sourceIndex - b.sourceIndex || a.position - b.position)
    .map(({ suggestion }) => suggestion);
}

function isPendingLTLQuoteSuggestion(
  suggestion: GenericSuggestion
): suggestion is SuggestedQuoteChange {
  if (
    suggestion.pipeline !== SuggestionPipelines.QuickQuote ||
    !isQuoteRequestActionable(suggestion)
  ) {
    return false;
  }

  const { mode } = (suggestion as SuggestedQuoteChange).suggested ?? {};

  return mode?.toUpperCase() === QuoteMode.LTL;
}
